import { useCallback, useRef } from "react";
import { useAuthHeader } from "../context/AuthContext";
import { safeFetch, safeFetchStreaming } from "../utils/safe-fetch";
import { UserFacingError } from "../utils/errors";
import { isHttpError, serverMessageSafeToShow, HttpError, REQUEST_FAILED_MESSAGE } from "@common/utils/http-error";
import { notifySessionInvalid } from "../utils/auth-events";

const SLOW_FETCH_THRESHOLD_MS = 15_000;

function userFacingErrorFor(httpError: HttpError): UserFacingError {
    const serverMessage = serverMessageSafeToShow(httpError.status, httpError.body);
    const userError = new UserFacingError(serverMessage ?? httpError.message, httpError);
    userError.status = httpError.status;
    return userError;
}

async function timedFetch(
    fetchFn: (url: string, options?: RequestInit) => Promise<Response>,
    url: string,
    options: RequestInit
): Promise<Response> {
    const start = performance.now();
    try {
        return await fetchFn(url, options);
    } finally {
        const elapsed = performance.now() - start;
        if (elapsed >= SLOW_FETCH_THRESHOLD_MS) {
            console.warn(`Slow fetch: ${url} took ${Math.round(elapsed)}ms`);
        }
    }
}

export const useDioptraFetch = () => {
    const authHeader = useAuthHeader();
    const authHeaderRef = useRef(authHeader);

    authHeaderRef.current = authHeader;

    const dioptraFetch = useCallback(async (url: string, options: RequestInit = {}) => {
        try {
            const response = await timedFetch(safeFetch, url, {
                ...options,
                headers: {
                    Authorization: authHeaderRef.current,
                    "Content-Type": "application/json",
                    ...options.headers
                }
            });

            return await response.json();
        } catch (error) {
            if (isHttpError(error)) {
                if (error.status === 401) {
                    console.warn(`[dioptra-auth] dioptraFetch: 401 from ${url} → notifySessionInvalid()`);
                    notifySessionInvalid();
                }
                throw userFacingErrorFor(error);
            }
            if (error instanceof UserFacingError) throw error;
            throw new UserFacingError(
                REQUEST_FAILED_MESSAGE,
                error instanceof Error ? error : new Error(String(error))
            );
        }
    }, []);

    const dioptraFetchStream = useCallback(async (url: string, options: RequestInit = {}) => {
        try {
            return await timedFetch(safeFetchStreaming, url, {
                ...options,
                headers: {
                    Authorization: authHeaderRef.current,
                    ...options.headers
                }
            });
        } catch (error) {
            if (!isHttpError(error)) throw error;
            if (error.status === 401) {
                console.warn(`[dioptra-auth] dioptraFetchStream: 401 from ${url} → notifySessionInvalid()`);
                notifySessionInvalid();
            }
            throw userFacingErrorFor(error);
        }
    }, []);

    return { dioptraFetch, dioptraFetchStream };
};
